import React, { useState } from 'react';
import { Button } from '../components/ui/Button';
import { Section } from '../components/ui/Section';
import { Card } from '../components/ui/Card';
import { Modal } from '../components/ui/Modal';
import { Moon, Wind, Zap, FileText, Home, Map, Clock, CheckCircle2, AlertCircle, ArrowRight, BookOpen } from 'lucide-react';
export function SalesPage() {
  const [activeModal, setActiveModal] = useState<'privacy' | 'terms' | null>(null);
  return <div className="min-h-screen bg-slate-950 text-slate-300 selection:bg-indigo-500/30" data-id="element-15">
      {/* Navigation / Header */}
      <nav className="fixed top-0 w-full z-50 bg-slate-950/80 backdrop-blur-md border-b border-slate-800/50" data-id="element-16">
        <div className="max-w-7xl mx-auto px-4 h-16 flex items-center justify-between" data-id="element-17">
          <div className="flex items-center gap-2" data-id="element-18">
            <Moon className="h-5 w-5 text-amber-100" data-id="element-19" />
            <span className="font-serif font-bold text-slate-100 text-lg tracking-wide" data-id="element-20">
              3AM RESET
            </span>
          </div>
          <Button variant="ghost" size="sm" scrollTo="pricing" data-id="element-21">
            Conseguir Guía
          </Button>
        </div>
      </nav>

      {/* HERO SECTION */}
      <Section className="pt-32 pb-20 md:pt-48 md:pb-32 relative overflow-hidden" data-id="element-22">
        {/* Background Ambient Glow */}
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[500px] bg-indigo-900/20 rounded-full blur-[120px] -z-10" data-id="element-23" />

        <div className="max-w-3xl mx-auto text-center" data-id="element-24">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-indigo-950/50 border border-indigo-900/50 text-indigo-200 text-sm mb-8 animate-fade-in" data-id="element-25">
            <Clock className="h-3.5 w-3.5" data-id="element-26" />
            <span data-id="element-27">Lectura de 4 minutos</span>
          </div>

          <h1 className="text-5xl md:text-7xl font-serif font-bold text-slate-100 mb-6 leading-tight" data-id="element-28">
            Son las <span className="text-amber-100" data-id="element-29">3:17 AM</span>.
          </h1>

          <p className="text-xl md:text-2xl text-slate-400 mb-8 leading-relaxed font-light" data-id="element-30">
            Abres los ojos y tu cuerpo ya está corriendo…{' '}
            <br className="hidden md:block" data-id="element-31" />
            aunque no te moviste.
          </p>

          <div className="p-6 bg-slate-900/50 border border-slate-800 rounded-lg max-w-xl mx-auto mb-12 text-left" data-id="element-32">
            <div className="flex gap-4" data-id="element-33">
              <AlertCircle className="h-6 w-6 text-indigo-400 shrink-0 mt-1" data-id="element-34" />
              <div data-id="element-35">
                <h3 className="text-slate-200 font-semibold mb-1" data-id="element-36">
                  No es "ansiedad"
                </h3>
                <p className="text-slate-400 text-sm" data-id="element-37">
                  Es una alarma biológica. Y tu mente está intentando apagarla
                  con pensamientos, lo cual solo la enciende más.
                </p>
              </div>
            </div>
          </div>

          <Button size="lg" scrollTo="pricing" className="group" data-id="element-38">
            Detener la alarma ahora
            <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" data-id="element-39" />
          </Button>
        </div>
      </Section>

      {/* PROBLEM AWARENESS */}
      <Section background="subtle" data-id="element-40">
        <div className="max-w-3xl mx-auto" data-id="element-41">
          <h2 className="text-3xl md:text-4xl font-serif text-slate-100 mb-8 text-center" data-id="element-42">
            El problema real <br data-id="element-43" />
            <span className="text-slate-500 text-2xl italic" data-id="element-44">
              (el que nadie te explica)
            </span>
          </h2>

          <div className="space-y-8 text-lg leading-relaxed text-slate-300" data-id="element-45">
            <p data-id="element-46">
              El corazón rápido. El pecho raro. Calor. Una sensación de "algo
              anda mal". Y lo peor no es el síntoma.
            </p>
            <p data-id="element-47">
              Lo peor es lo que viene después:{' '}
              <strong className="text-indigo-200" data-id="element-48">
                la mente buscando una explicación como si fuera una emergencia.
              </strong>
            </p>

            <Card className="my-10 border-l-4 border-l-amber-100/50" data-id="element-49">
              <p className="font-serif text-xl text-slate-200 italic" data-id="element-50">
                "No quieres despertar a nadie. No quieres hacer drama. Solo
                quieres volver a dormir."
              </p>
            </Card>

            <p data-id="element-51">
              A las 3AM, si tu cuerpo entra en modo alarma, tu mente hace lo
              único que sabe hacer: buscar una razón. Pero cuando intentas
              razonar en plena activación, el cuerpo lo interpreta como:
            </p>

            <div className="bg-red-950/20 border border-red-900/30 p-4 rounded-lg text-center text-red-200 font-medium" data-id="element-52">
              "Si estamos analizando así… es porque SÍ hay peligro."
            </div>

            <p data-id="element-53">
              Entonces sube la alarma. Y sube el miedo. Y sube el insomnio. Por
              eso los consejos normales (tips sueltos, suplementos, "piensa
              positivo") fallan: intentan arreglar con la mente lo que empezó en
              el cuerpo.
            </p>
          </div>
        </div>
      </Section>

      {/* SOLUTION REVEAL - LA ESCALERA */}
      <Section data-id="element-54">
        <div className="text-center mb-16" data-id="element-55">
          <span className="text-amber-100 font-medium tracking-widest uppercase text-sm" data-id="element-56">
            La Solución
          </span>
          <h2 className="text-4xl md:text-5xl font-serif text-slate-100 mt-4 mb-6" data-id="element-57">
            La Escalera 3AM
          </h2>
          <p className="text-slate-400 max-w-2xl mx-auto" data-id="element-58">
            Un protocolo en orden que corta la alarma desde el cuerpo, no desde
            el debate mental.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-5 gap-4" data-id="element-59">
          {[{
          step: '1',
          title: 'AIRE',
          desc: 'Los primeros 90 segundos',
          icon: Wind,
          color: 'text-cyan-300'
        }, {
          step: '2',
          title: 'DESCARGA',
          desc: 'Sacar adrenalina sin ejercicio',
          icon: Zap,
          color: 'text-amber-300'
        }, {
          step: '3',
          title: 'GUION',
          desc: 'Frases para cortar la espiral',
          icon: FileText,
          color: 'text-emerald-300'
        }, {
          step: '4',
          title: 'ENTORNO',
          desc: 'Tu cueva: luz y sonido',
          icon: Home,
          color: 'text-indigo-300'
        }, {
          step: '5',
          title: 'PLAN',
          desc: 'Qué hacer si no duermes',
          icon: Map,
          color: 'text-purple-300'
        }].map(item => <Card key={item.title} className="relative overflow-hidden group hover:border-slate-600 transition-colors" data-id="element-60">
              <div className="absolute top-0 right-0 p-4 opacity-10 font-serif text-6xl font-bold" data-id="element-61">
                {item.step}
              </div>
              <item.icon className={`h-8 w-8 ${item.color} mb-4`} data-id="element-62" />
              <h3 className="text-lg font-bold text-slate-100 mb-2" data-id="element-63">
                {item.title}
              </h3>
              <p className="text-sm text-slate-400" data-id="element-64">{item.desc}</p>
            </Card>)}
        </div>

        <div className="mt-12 text-center" data-id="element-65">
          <p className="text-slate-400 italic" data-id="element-66">
            "No te pide pensar bonito. Te pide ejecutar pasos pequeños."
          </p>
        </div>
      </Section>

      {/* PRODUCT SHOWCASE */}
      <Section background="subtle" data-id="element-67">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center" data-id="element-68">
          <div className="order-2 lg:order-1" data-id="element-69">
            <h2 className="text-3xl md:text-4xl font-serif text-slate-100 mb-8" data-id="element-70">
              Qué recibes en <span className="text-amber-100" data-id="element-71">3AM RESET</span>
            </h2>

            <ul className="space-y-4" data-id="element-72">
              {['La Escalera 3AM completa (con tiempos reales)', '3 respiraciones anti-alarma (eliges 1)', '6 micro-descargas (eliges 2)', '10 guiones anti-espiral (eliges 1)', "Setup de 'mesa de noche' minimalista", 'Protocolo sin pantalla si no logras dormir', 'Plan de 14 noches para automatizar el hábito'].map((feature, i) => <li key={i} className="flex items-start gap-3" data-id="element-73">
                  <CheckCircle2 className="h-5 w-5 text-indigo-400 mt-0.5 shrink-0" data-id="element-74" />
                  <span className="text-slate-300" data-id="element-75">{feature}</span>
                </li>)}
            </ul>

            <div className="mt-10 p-6 bg-slate-950 rounded-xl border border-slate-800" data-id="element-76">
              <h4 className="font-serif text-slate-100 mb-2" data-id="element-77">
                Formato PDF Digital
              </h4>
              <p className="text-sm text-slate-400" data-id="element-78">
                Diseñado para leerse en modo oscuro, con letra grande y clara.
                Perfecto para tener en el celular sin que la luz te despierte
                más.
              </p>
            </div>
          </div>

          <div className="order-1 lg:order-2 relative" data-id="element-79">
            <div className="absolute inset-0 bg-indigo-500/20 blur-[100px] rounded-full" data-id="element-80" />
            <img src="https://cdn.magicpatterns.com/uploads/peW6dD77HFenND5j9gDy5q/imagen_14_dias.png" alt="3AM Reset Kit en dispositivos" className="relative z-10 rounded-xl shadow-2xl border border-slate-800/50 w-full" data-id="element-81" />
          </div>
        </div>
      </Section>

      {/* AUDIENCE FILTER */}
      <Section data-id="element-82">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto" data-id="element-83">
          <div className="p-8 rounded-2xl bg-indigo-950/10 border border-indigo-900/30" data-id="element-84">
            <h3 className="text-xl font-serif text-indigo-200 mb-4 flex items-center gap-2" data-id="element-85">
              <CheckCircle2 className="h-5 w-5" data-id="element-86" /> Es para ti si...
            </h3>
            <ul className="space-y-3 text-slate-300 text-sm" data-id="element-87">
              <li data-id="element-88">• Te despiertas con ansiedad física de madrugada</li>
              <li data-id="element-89">• Necesitas algo práctico, privado y claro</li>
              <li data-id="element-90">• Estás cansada de sentirte "rara" y sola con esto</li>
              <li data-id="element-91">• Quieres recuperar control, aunque sea 10% por noche</li>
            </ul>
          </div>

          <div className="p-8 rounded-2xl bg-slate-900/30 border border-slate-800" data-id="element-92">
            <h3 className="text-xl font-serif text-slate-400 mb-4 flex items-center gap-2" data-id="element-93">
              <AlertCircle className="h-5 w-5" data-id="element-94" /> No es para ti si...
            </h3>
            <ul className="space-y-3 text-slate-400 text-sm" data-id="element-95">
              <li data-id="element-96">• Buscas diagnóstico o tratamiento médico</li>
              <li data-id="element-97">• Quieres promesas de curas mágicas</li>
              <li data-id="element-98">• Estás en una emergencia médica</li>
            </ul>
          </div>
        </div>
      </Section>

      {/* PRICING & CTA */}
      <Section id="pricing" className="pt-10 pb-32 scroll-mt-24" data-id="element-99">
        <div className="max-w-lg mx-auto" data-id="element-100">
          <Card className="border-t-4 border-t-amber-100 shadow-[0_0_50px_rgba(0,0,0,0.5)] relative overflow-hidden" data-id="element-101">
            <div className="absolute top-0 right-0 w-32 h-32 bg-amber-100/10 blur-[50px] rounded-full" data-id="element-102" />

            <div className="text-center mb-8" data-id="element-103">
              <h2 className="text-3xl font-serif text-slate-100 mb-2" data-id="element-104">
                3AM RESET
              </h2>
              <p className="text-slate-400" data-id="element-105">
                El kit de emergencia para tus noches
              </p>
            </div>

            <div className="flex justify-center items-baseline mb-8" data-id="element-106">
              <span className="text-5xl font-bold text-slate-100" data-id="element-107">$6.37</span>
              <span className="text-slate-500 ml-2 line-through" data-id="element-108">$19</span>
            </div>

            <div className="space-y-4 mb-8" data-id="element-109">
              <div className="flex items-center gap-3 text-sm text-slate-300" data-id="element-110">
                <BookOpen className="h-4 w-4 text-amber-100" data-id="element-111" />
                <span data-id="element-112">Acceso inmediato al PDF</span>
              </div>
              <div className="flex items-center gap-3 text-sm text-slate-300" data-id="element-113">
                <Moon className="h-4 w-4 text-amber-100" data-id="element-114" />
                <span data-id="element-115">Diseño oscuro (no lastima la vista)</span>
              </div>
              <div className="flex items-center gap-3 text-sm text-slate-300" data-id="element-116">
                <CheckCircle2 className="h-4 w-4 text-amber-100" data-id="element-117" />
                <span data-id="element-118">Garantía de claridad paso a paso</span>
              </div>
            </div>

            <Button fullWidth size="lg" className="text-lg mb-4" href="https://bienestarenminutos.com/cart/42929809588314:1" data-id="element-119">
              Consigue 3AM RESET ahora
            </Button>

            <p className="text-xs text-center text-slate-500" data-id="element-120">
              Pago seguro. Entrega digital inmediata a tu correo.
            </p>
          </Card>

          <div className="mt-12 text-center space-y-4" data-id="element-121">
            <h3 className="text-xl font-serif text-slate-200" data-id="element-122">
              La promesa realista
            </h3>
            <p className="text-slate-400" data-id="element-123">
              No te voy a prometer "nunca más ansiedad".
              <br data-id="element-124" />
              Te voy a prometer algo mejor:{' '}
              <strong className="text-slate-200" data-id="element-125">
                Que la próxima vez que pase, no vas a improvisar. Vas a tener un
                plan.
              </strong>
            </p>
          </div>
        </div>
      </Section>

      {/* FOOTER */}
      <footer className="py-8 border-t border-slate-900" data-id="element-126">
        <div className="max-w-7xl mx-auto px-4" data-id="element-127">
          <div className="flex flex-col items-center gap-4" data-id="element-128">
            <div className="flex items-center gap-1 text-slate-600 text-sm" data-id="element-129">
              <Moon className="h-4 w-4 text-amber-100/40" data-id="element-130" />
              <span data-id="element-131">© {new Date().getFullYear()} 3AM RESET</span>
            </div>

            <div className="flex items-center gap-6" data-id="element-132">
              <button onClick={() => setActiveModal('privacy')} className="text-slate-500 hover:text-slate-300 text-sm transition-colors" data-id="element-133">
                Política de Privacidad
              </button>
              <span className="text-slate-800" data-id="element-134">·</span>
              <button onClick={() => setActiveModal('terms')} className="text-slate-500 hover:text-slate-300 text-sm transition-colors" data-id="element-135">
                Términos y Condiciones
              </button>
            </div>

            <p className="text-xs text-slate-700 max-w-md text-center" data-id="element-136">
              Este producto es una guía educativa y no sustituye el consejo
              médico profesional, diagnóstico o tratamiento.
            </p>
          </div>
        </div>
      </footer>

      {/* PRIVACY POLICY MODAL */}
      <Modal isOpen={activeModal === 'privacy'} onClose={() => setActiveModal(null)} title="Política de Privacidad" data-id="element-137">
        <p data-id="element-138">
          <strong className="text-slate-100" data-id="element-139">Última actualización:</strong>{' '}
          Enero 2025
        </p>

        <h3 className="text-slate-100 font-semibold mt-4" data-id="element-140">
          1. Información que recopilamos
        </h3>
        <p data-id="element-141">
          Al adquirir 3AM RESET, recopilamos únicamente la información necesaria
          para procesar tu compra: nombre, dirección de correo electrónico y
          datos de pago. No almacenamos información de tarjetas de crédito
          directamente; los pagos son procesados por plataformas seguras de
          terceros.
        </p>

        <h3 className="text-slate-100 font-semibold mt-4" data-id="element-142">
          2. Uso de la información
        </h3>
        <p data-id="element-143">Tu información se utiliza exclusivamente para:</p>
        <ul className="list-disc pl-5 space-y-1" data-id="element-144">
          <li data-id="element-145">Entregar el producto digital a tu correo electrónico.</li>
          <li data-id="element-146">Enviarte actualizaciones relacionadas con tu compra.</li>
          <li data-id="element-147">Responder a consultas de soporte.</li>
        </ul>

        <h3 className="text-slate-100 font-semibold mt-4" data-id="element-148">
          3. Protección de datos
        </h3>
        <p data-id="element-149">
          Implementamos medidas de seguridad estándar de la industria para
          proteger tu información personal. No vendemos, intercambiamos ni
          transferimos tu información a terceros, excepto los necesarios para
          procesar tu compra.
        </p>

        <h3 className="text-slate-100 font-semibold mt-4" data-id="element-150">4. Cookies</h3>
        <p data-id="element-151">
          Este sitio puede utilizar cookies para mejorar la experiencia del
          usuario. Puedes configurar tu navegador para rechazar cookies, aunque
          esto podría afectar algunas funcionalidades.
        </p>

        <h3 className="text-slate-100 font-semibold mt-4" data-id="element-152">5. Tus derechos</h3>
        <p data-id="element-153">
          Puedes solicitar acceso, corrección o eliminación de tus datos
          personales en cualquier momento enviando una solicitud a través de
          nuestros canales de comunicación.
        </p>
      </Modal>

      {/* TERMS & CONDITIONS MODAL */}
      <Modal isOpen={activeModal === 'terms'} onClose={() => setActiveModal(null)} title="Términos y Condiciones" data-id="element-154">
        <p data-id="element-155">
          <strong className="text-slate-100" data-id="element-156">Última actualización:</strong>{' '}
          Enero 2025
        </p>

        <h3 className="text-slate-100 font-semibold mt-4" data-id="element-157">
          1. Producto digital
        </h3>
        <p data-id="element-158">
          3AM RESET es un producto digital en formato PDF. Al completar la
          compra, recibirás acceso inmediato al archivo a través de tu correo
          electrónico. Este producto es para uso personal y no puede ser
          redistribuido, revendido ni compartido.
        </p>

        <h3 className="text-slate-100 font-semibold mt-4" data-id="element-159">
          2. Naturaleza educativa
        </h3>
        <p data-id="element-160">
          Este producto es una guía educativa de bienestar y autorregulación
          emocional. No constituye consejo médico, diagnóstico ni tratamiento
          profesional. Si experimentas síntomas severos o persistentes, consulta
          a un profesional de la salud.
        </p>

        <h3 className="text-slate-100 font-semibold mt-4" data-id="element-161">
          3. Política de reembolso
        </h3>
        <p data-id="element-162">
          Debido a la naturaleza digital del producto y su entrega inmediata,
          todas las ventas son finales. Si tienes algún problema con tu compra,
          contáctanos y haremos lo posible por ayudarte.
        </p>

        <h3 className="text-slate-100 font-semibold mt-4" data-id="element-163">
          4. Propiedad intelectual
        </h3>
        <p data-id="element-164">
          Todo el contenido de 3AM RESET, incluyendo textos, diseños, gráficos y
          metodología, está protegido por derechos de autor. Queda prohibida su
          reproducción total o parcial sin autorización expresa.
        </p>

        <h3 className="text-slate-100 font-semibold mt-4" data-id="element-165">
          5. Limitación de responsabilidad
        </h3>
        <p data-id="element-166">
          3AM RESET se proporciona "tal cual". No garantizamos resultados
          específicos. Los resultados pueden variar según cada persona. El uso
          del protocolo es bajo tu propia responsabilidad.
        </p>

        <h3 className="text-slate-100 font-semibold mt-4" data-id="element-167">6. Modificaciones</h3>
        <p data-id="element-168">
          Nos reservamos el derecho de modificar estos términos en cualquier
          momento. Las modificaciones entrarán en vigor inmediatamente después
          de su publicación en esta página.
        </p>
      </Modal>
    </div>;
}